<script>
  import { createEventDispatcher, onDestroy, onMount } from 'svelte';
  import { ArrowDown, ArrowLeft, File, FileAudio, FileImage, FileVideo, Search, Trash2, Undo2, X } from 'lucide-svelte';
  import { api } from '$lib/api.js';

  export let conversationId;

  const dispatch = createEventDispatcher();
  let files = [];
  let cloud = null;
  let hasMore = false;
  let loadingMore = false;
  let nextOffset = 0;
  let loading = true;
  let failedToLoad = false;
  let busyId = '';
  let error = '';
  let keyword = '';
  let filter = 'all';
  let showDeleted = false;
  let clock = Date.now();
  let timer;

  $: filteredFiles = files.filter((file) => {
    if (!showDeleted && file.deleted_at) return false;
    if (showDeleted && !file.deleted_at) return false;
    const query = keyword.trim().toLocaleLowerCase();
    if (query && !String(file.name || '').toLocaleLowerCase().includes(query)) return false;
    return filter === 'all' || file.bucket === filter;
  });

  onMount(() => {
    loadFiles();
    timer = setInterval(() => (clock = Date.now()), 1000);
  });

  onDestroy(() => clearInterval(timer));

  async function loadFiles(append = false) {
    loading = !append;
    loadingMore = append;
    error = '';
    if (!append) failedToLoad = false;
    try {
      const offset = append ? nextOffset : 0;
      const response = await api.get(`/api/conversations/${conversationId}/my-cloud/files?includeDeleted=true&limit=80&offset=${offset}`);
      const page = (response.files || []).map((file) => ({
        ...file,
        bucket: getBucket(file.mime, file.category),
        restoreExpiresAt: file.deleted_at && file.restoreSeconds > 0
          ? Date.now() + Number(file.restoreSeconds) * 1000
          : 0
      }));
      files = append ? [...files, ...page] : page;
      nextOffset = offset + page.length;
      hasMore = Boolean(response.hasMore);
      cloud = response.cloud || null;
      dispatch('updated', { cloud });
    } catch (requestError) {
      error = requestError?.message || 'Không tải được danh sách tệp.';
      failedToLoad = true;
    } finally {
      loading = false;
      loadingMore = false;
    }
  }

  function getBucket(mime = '', category = '') {
    if (String(mime).startsWith('image/') || category === 'images') return 'image';
    if (String(mime).startsWith('video/') || category === 'videos') return 'video';
    if (String(mime).startsWith('audio/') || category === 'audios') return 'audio';
    return 'file';
  }

  function formatBytes(value = 0) {
    const bytes = Number(value || 0);
    if (bytes < 1024) return `${bytes} B`;
    const units = ['KB', 'MB', 'GB', 'TB'];
    let size = bytes;
    let unit = -1;
    do { size /= 1024; unit += 1; } while (size >= 1024 && unit < units.length - 1);
    return `${size.toFixed(size >= 10 ? 0 : 1)} ${units[unit]}`;
  }

  function remainingSeconds(file) {
    return Math.max(0, Math.ceil((Number(file.restoreExpiresAt || 0) - clock) / 1000));
  }

  async function removeFile(file) {
    if (busyId) return;
    busyId = file.id;
    error = '';
    try {
      const result = await api.delete(`/api/conversations/${conversationId}/my-cloud/files/${file.id}`);
      const expiry = new Date(result.deletedAt).getTime() + Number(result.restoreSeconds || 10) * 1000;
      files = files.map((item) => item.id === file.id
        ? { ...item, deleted_at: result.deletedAt, restoreExpiresAt: expiry }
        : item);
      cloud = result.cloud || cloud;
      dispatch('updated', { cloud });
    } catch (requestError) {
      error = requestError?.message || 'Không thể xóa tệp.';
    } finally {
      busyId = '';
    }
  }

  async function restoreFile(file) {
    if (busyId || remainingSeconds(file) <= 0) return;
    busyId = file.id;
    error = '';
    try {
      const result = await api.post(`/api/conversations/${conversationId}/my-cloud/files/${file.id}/restore`, {});
      files = files.map((item) => item.id === file.id
        ? { ...item, deleted_at: null, deleted_by: null, restoreExpiresAt: 0 }
        : item);
      cloud = result.cloud || cloud;
      dispatch('updated', { cloud });
    } catch (requestError) {
      const message = requestError?.message || 'Không thể khôi phục tệp.';
      await loadFiles();
      error = message;
    } finally {
      busyId = '';
    }
  }

  function iconFor(file) {
    if (file.bucket === 'image') return FileImage;
    if (file.bucket === 'video') return FileVideo;
    if (file.bucket === 'audio') return FileAudio;
    return File;
  }
</script>

<div class="fixed inset-0 z-[100] flex items-stretch justify-center bg-slate-950/45 p-0 backdrop-blur-[2px] sm:items-center sm:p-5" role="presentation">
  <section class="flex h-full w-full max-w-6xl flex-col overflow-hidden border border-[#ccc] bg-white shadow-2xl sm:h-[min(92vh,900px)] sm:rounded-xl" role="dialog" aria-modal="true" aria-label="Quản lý My Documents">
    <header class="flex shrink-0 items-center gap-3 px-4 py-3 sm:px-6">
      <button type="button" class="grid h-9 w-9 place-items-center rounded-full text-slate-600 hover:bg-slate-100" aria-label="Đóng" onclick={() => dispatch('close')}>
        <ArrowLeft size={19} />
      </button>
      <div class="min-w-0 flex-1">
        <h2 class="truncate text-lg font-semibold text-slate-950">My Documents</h2>
        <p class="text-xs text-slate-500">Quản lý tệp trong My Cloud</p>
      </div>
      <button type="button" class="hidden h-9 w-9 place-items-center rounded-full text-slate-600 hover:bg-slate-100 sm:grid" aria-label="Đóng" onclick={() => dispatch('close')}>
        <X size={18} />
      </button>
    </header>

    <div class="flex shrink-0 flex-col gap-4 p-4 sm:px-6">
      <div class="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p class="text-sm font-semibold text-slate-900">Dung lượng đang dùng</p>
          <p class="mt-1 text-xs text-slate-500">{formatBytes(cloud?.usedBytes)} / {formatBytes(cloud?.quotaBytes || 1024 ** 3)}</p>
        </div>
        <div class="flex rounded-lg bg-slate-100 p-1" role="tablist" aria-label="Trạng thái tệp">
          <button type="button" role="tab" aria-selected={!showDeleted} class={`rounded-md px-3 py-1.5 text-sm font-medium ${!showDeleted ? 'bg-white text-blue-700 shadow-sm' : 'text-slate-600'}`} onclick={() => (showDeleted = false)}>Tất cả</button>
          <button type="button" role="tab" aria-selected={showDeleted} class={`rounded-md px-3 py-1.5 text-sm font-medium ${showDeleted ? 'bg-white text-blue-700 shadow-sm' : 'text-slate-600'}`} onclick={() => (showDeleted = true)}>Đã xóa</button>
        </div>
      </div>
      <div class="flex flex-col gap-3 sm:flex-row sm:items-center">
        <label class="flex h-10 min-w-0 flex-1 items-center gap-2 rounded-lg border border-[#ccc] px-3 focus-within:border-blue-500">
          <Search size={17} class="shrink-0 text-slate-400" />
          <input class="min-w-0 flex-1 border-0 bg-transparent text-sm outline-none" bind:value={keyword} placeholder="Tìm tên tệp" aria-label="Tìm tên tệp" />
        </label>
        <div class="flex gap-1 overflow-x-auto" aria-label="Lọc theo loại">
          {#each [{ id: 'all', label: 'Tất cả loại' }, { id: 'image', label: 'Ảnh' }, { id: 'video', label: 'Video' }, { id: 'file', label: 'File' }, { id: 'audio', label: 'Âm thanh' }] as option (option.id)}
            <button type="button" class={`shrink-0 rounded-md px-3 py-2 text-xs font-medium ${filter === option.id ? 'bg-blue-50 text-blue-700' : 'text-slate-600 hover:bg-slate-50'}`} onclick={() => (filter = option.id)}>{option.label}</button>
          {/each}
        </div>
      </div>
    </div>

    <div class="min-h-0 flex-1 overflow-auto">
      {#if loading}
        <div class="grid h-full place-items-center text-sm text-slate-500">Đang tải tệp...</div>
      {:else if failedToLoad}
        <div class="grid h-full place-items-center px-6 text-center">
          <div>
            <File size={30} class="mx-auto text-red-300" />
            <p class="mt-3 text-sm font-medium text-slate-700">Không thể tải My Documents</p>
            <button type="button" class="mt-3 rounded-md border border-[#ccc] px-3 py-2 text-sm font-semibold text-blue-700 hover:bg-blue-50" onclick={() => loadFiles()}>Thử lại</button>
          </div>
        </div>
      {:else if filteredFiles.length === 0}
        <div class="grid h-full place-items-center px-6 text-center">
          <div>
            <File size={30} class="mx-auto text-slate-300" />
            <p class="mt-3 text-sm font-medium text-slate-700">{showDeleted ? 'Thùng rác đang trống' : 'Chưa có tệp phù hợp'}</p>
          </div>
        </div>
      {:else}
        <div class="divide-y divide-slate-100 px-4 sm:px-6">
          {#each filteredFiles as file (file.id)}
          <article class="flex min-h-[68px] items-center gap-3 border-b border-[#ccc] py-3 last:border-b-0">
              {#if file.bucket === 'image'}
                <img src={file.url} alt="" class="h-11 w-11 shrink-0 rounded-md border border-slate-200 object-cover" loading="lazy" />
              {:else}
                <div class="grid h-11 w-11 shrink-0 place-items-center rounded-md bg-slate-100 text-slate-600">
                  <svelte:component this={iconFor(file)} size={20} />
                </div>
              {/if}
              <div class="min-w-0 flex-1">
                <p class="truncate text-sm font-medium text-slate-900">{file.name || 'Tệp không tên'}</p>
                <p class="mt-1 text-xs text-slate-500">{formatBytes(file.size)}<span class="px-1.5">·</span>{new Date(file.created_at).toLocaleDateString('vi-VN')}</p>
              </div>
              {#if file.deleted_at}
                {#if remainingSeconds(file) > 0}
                  <button type="button" class="inline-flex shrink-0 items-center gap-1.5 rounded-md bg-blue-50 px-3 py-2 text-xs font-semibold text-blue-700 hover:bg-blue-100 disabled:opacity-50" disabled={busyId === file.id} onclick={() => restoreFile(file)}>
                    <Undo2 size={15} /> Khôi phục {remainingSeconds(file)}s
                  </button>
                {:else}
                  <span class="shrink-0 text-xs text-slate-400">Hết hạn khôi phục</span>
                {/if}
              {:else}
                <a href={file.url} download={file.name} class="grid h-9 w-9 shrink-0 place-items-center rounded-md text-slate-500 hover:bg-slate-100" aria-label={`Tải ${file.name}`} title="Tải xuống"><ArrowDown size={17} /></a>
                <button type="button" class="grid h-9 w-9 shrink-0 place-items-center rounded-md text-slate-500 hover:bg-red-50 hover:text-red-600 disabled:opacity-50" aria-label={`Xóa ${file.name}`} title="Xóa" disabled={busyId === file.id} onclick={() => removeFile(file)}><Trash2 size={17} /></button>
              {/if}
            </article>
          {/each}
        </div>
      {/if}
      {#if !loading && hasMore}
        <div class="border-t border-slate-100 p-4 text-center">
          <button type="button" class="rounded-md px-4 py-2 text-sm font-semibold text-blue-700 hover:bg-blue-50 disabled:opacity-50" disabled={loadingMore} onclick={() => loadFiles(true)}>
            {loadingMore ? 'Đang tải...' : 'Tải thêm tệp'}
          </button>
        </div>
      {/if}
    </div>
  </section>
</div>
